import { Overlay } from '@angular/cdk/overlay';
import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  Input,
  OnChanges,
  OnInit,
  SimpleChange,
  SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { firstValueFrom, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { SnapshotService } from '@features/budget/services/snapshot.service';
import { SnapshotModalComponent } from '@features/snapshot-modal/snapshot-modal.component';

import { BudgetStore } from '@models/budget/budget.store';

import { IconComponent } from '@shared/components/icon/icon.component';
import { ConfirmationModalComponent } from '@shared/components/modals/confirmation-modal/confirmation-modal.component';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import { BudgetSnapshotType, EventType } from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { EventQuery } from '@shared/store/event/event.query';

import {
  BudgetCompareMenuSnapshotChangeFn,
  BudgetCompareMenuSnapshotDisabled,
  BudgetCompareMenuSnapshotFormControl,
  BudgetCompareMenuSnapshotList,
  BudgetCompareMenuSnapshotLoading,
  BudgetCompareMenuSnapshotRefreshFn,
  BudgetCompareMenuSnapshotShowDelete,
  BudgetCompareMenuSnapshotShowEdit,
  BudgetCompareMenuSnapshotValue,
  defaultSnapshotChangeFn,
  defaultSnapshotRefreshFn,
} from '../../models/budget-compare-menu.models';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgSelectModule, ReactiveFormsModule, TooltipDirective, IconComponent, DatePipe],
  selector: 'aux-compare-dropdown-trial-insights',
  styles: [
    `
      ::ng-deep .ng-option-selected::after {
        display: none;
      }
    `,
  ],
  templateUrl: './compare-dropdown.component.html',
})
export class CompareDropdownComponent implements OnChanges, OnInit {
  overlay = inject(Overlay);

  private budgetStore = inject(BudgetStore);
  private readonly destroyRef = inject(DestroyRef);
  private eventQuery = inject(EventQuery);
  private overlayService = inject(OverlayService);
  private snapshotService = inject(SnapshotService);

  BUDGET_SNAPSHOT_USER_CREATED = BudgetSnapshotType.BUDGET_SNAPSHOT_USER_CREATED;
  @Input() snapshotChangeFn: BudgetCompareMenuSnapshotChangeFn;
  @Input() snapshotDisabled: BudgetCompareMenuSnapshotDisabled;
  @Input() snapshotFormControl: BudgetCompareMenuSnapshotFormControl;
  @Input() snapshotList: BudgetCompareMenuSnapshotList;
  @Input() snapshotLoading: BudgetCompareMenuSnapshotLoading;
  @Input() snapshotRefreshFn: BudgetCompareMenuSnapshotRefreshFn;
  @Input() snapshotShowDelete: BudgetCompareMenuSnapshotShowDelete;
  @Input() snapshotShowEdit: BudgetCompareMenuSnapshotShowEdit;
  @Input() snapshotValue: BudgetCompareMenuSnapshotValue;

  ngOnChanges(changes: SimpleChanges): void {
    if ('snapshotList' in changes) {
      this.processSnapshotList(changes.snapshotList);
    }

    if ('snapshotLoading' in changes) {
      this.processSnapshotLoading(changes.snapshotLoading);
    }
  }

  ngOnInit(): void {
    this.eventQuery
      .selectProcessingEvent$(EventType.CREATE_TRIAL_BUDGET_SNAPSHOT)
      .subscribe((isProcessing) => {
        if (isProcessing === false) {
          this.overlayService.showFieldTooltip(
            'compare-snapshot-dropdown',
            'Snapshot successfully created'
          );
          void firstValueFrom(this.snapshotService.getSnapshotList());
        }
      });

    this.snapshotFormControl.setValue(this.snapshotFormControl.defaultValue);

    this.snapshotFormControl.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap((snapshotName) => {
          this.snapshotChangeFn(snapshotName || '');

          if (!snapshotName) {
            this.snapshotService.setOriginalBudgetData();

            return of();
          }

          return this.snapshotService.getBudgetSnapshots(snapshotName);
        })
      )
      .subscribe();
  }

  constructor() {
    this.snapshotFormControl = new FormControl<string>('');
    this.snapshotList = [];
    this.snapshotValue = '';
    this.snapshotDisabled = false;
    this.snapshotShowEdit = true;
    this.snapshotShowDelete = true;
    this.snapshotLoading = false;
    this.snapshotChangeFn = defaultSnapshotChangeFn;
    this.snapshotRefreshFn = defaultSnapshotRefreshFn;
  }

  deleteSnapshot(
    event: Event,
    snapshot: {
      create_date: string;
      id: string;
      label: string;
      snapshot_type: string;
      value: string;
    }
  ) {
    event.stopPropagation();

    this.overlayService.openPopup<
      { message: string },
      boolean | undefined,
      ConfirmationModalComponent
    >({
      content: ConfirmationModalComponent,
      data: {
        message: `Are you sure you want to remove ${snapshot.label} plan?`,
      },
      settings: {
        header: 'Remove Plan',
        primaryButton: {
          action: async (instance) => {
            instance?.ref.close();
            await this.emitUpdatedValue(snapshot.label, undefined, () =>
              this.snapshotService.removeBudgetSnapshots(snapshot)
            );
          },
          label: 'Remove',
        },
      },
    });
  }

  async editSnapshot(
    event: Event,
    snapshot: {
      create_date: string;
      id: string;
      label: string;
      snapshot_type: string;
      value: string;
    }
  ) {
    event.stopPropagation();
    const name = snapshot.label;
    const { data } = await firstValueFrom(
      this.overlayService.openPopup<{ name?: string }, { name?: string }>({
        data: { name },
        modal: SnapshotModalComponent,
        settings: { header: 'Name Budget Snapshot' },
      }).afterClosed$
    );

    if (data?.name) {
      const n = data.name;

      await this.emitUpdatedValue(
        name,
        n,
        () => {
          return this.snapshotService.updateSnapshots(snapshot, n);
        },
        () => this.snapshotFormControl.setValue(n)
      );
    }
  }

  private async emitUpdatedValue(
    value: string,
    newName: string | undefined,
    callback: () => Promise<void>,
    updateControlCallback?: VoidFunction
  ) {
    if (value === this.snapshotFormControl.value) {
      this.snapshotChangeFn(newName || '');
      this.budgetStore.setLoading(true);

      if (updateControlCallback) {
        updateControlCallback();
      }

      // await callback();
      this.snapshotRefreshFn();
    } else {
      await callback();
    }

    this.budgetStore.setLoading(false);
  }

  groupByFn(option: {
    create_date: string;
    id: string;
    label: string;
    snapshot_type: string;
    value: string;
  }) {
    switch (option.snapshot_type) {
      case BudgetSnapshotType.BUDGET_SNAPSHOT_USER_CREATED:
        return 'MANUAL SNAPSHOTS';
      default:
        return 'AUTOMATIC SNAPSHOTS ';
    }
  }

  processSnapshotList(change: SimpleChange): void {
    if ('currentValue' in change) {
      const options = change.currentValue as BudgetCompareMenuSnapshotList;

      const isValueExist = options.find(({ value }) => value === this.snapshotFormControl.value);

      if (!isValueExist) {
        this.snapshotFormControl.setValue(this.snapshotFormControl.defaultValue);
      }
    }
  }

  processSnapshotLoading(change: SimpleChange): void {
    if ('currentValue' in change) {
      const loading = change.currentValue as boolean;

      if (loading || this.snapshotDisabled) {
        this.snapshotFormControl.disable();
      } else {
        this.snapshotFormControl.enable();
      }
    }
  }
}
